package com.exura.pages.settings

import androidx.compose.runtime.*
import com.exura.components.buttons.AppButton
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonPrimaryVariant
import com.exura.components.inputs.InlineInputGhostVariant
import com.exura.components.inputs.InlineTextInput
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraCommentaryTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.GeneralListEntry
import com.exura.components.pageTypography.HoverableListEntryVariant
import com.exura.components.pageTypography.PageErrorContent
import com.exura.components.pageTypography.PageMainContent
import com.exura.components.pageTypography.PageNavigationHeaderSection
import com.exura.components.pageTypography.PageSecondaryContent
import com.exura.components.pageTypography.TitledSection
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.features.localization.commonLocalization
import com.exura.features.userprofile.domain.model.DoctorProfile
import com.exura.features.userprofile.domain.model.OrganizationProfile
import com.exura.features.userprofile.domain.model.ProviderProfile
import com.exura.navigation.Route
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.css.div

@Page(Route.SETTINGS)
@Layout(".components.layouts.NavigableLayout")
@Composable
fun NavigableLayoutScope.ProviderProfileSettingsPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val controller = rememberProviderProfileSettingsController(scope, context)
    val state = controller.uiState

    Column(modifier = Modifier.fillMaxSize()) {
        PageNavigationHeaderSection(
            profileLocalization.current.profilePageTitle,
            isPageWorking = state.isLoading,
            onBack = null,
        )

        when {
            state.isLoading && state.profile == null -> Loader()
            state.error != null -> PageErrorContent(errorMessage = state.error)
            state.profile != null -> ProfileSettingsView(
                modifier = Modifier.weight(1f),
                profile = state.profile,
                editingField = state.editingField,
                editedValues = state.editedValues,
                onEvent = controller::handleEvent,
                showSnackbar = ::showSnackbar,
            )
        }
    }
}

@Composable
private fun NavigableLayoutScope.ProfileSettingsView(
    modifier: Modifier = Modifier,
    profile: ProviderProfile,
    editingField: String?,
    editedValues: Map<String, String>,
    onEvent: (ProfileSettingsEvent) -> Unit,
    showSnackbar: (String) -> Unit,
) {
    PageMainContent(modifier) {
        Column(modifier = Modifier.fillMaxSize()) {
            PageSecondaryContent(modifier = Modifier.weight(1f)) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(Dimens.pageSectionDefaultGap)
                ) {
                    TitledSection(title = profileLocalization.current.basicInformation) {
                        ProfileFieldsList(
                            profile = profile,
                            editingField = editingField,
                            editedValues = editedValues,
                            onEvent = onEvent,
                            showSnackbar = showSnackbar,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun NavigableLayoutScope.ProfileFieldsList(
    profile: ProviderProfile,
    editingField: String?,
    editedValues: Map<String, String>,
    onEvent: (ProfileSettingsEvent) -> Unit,
    showSnackbar: (String) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        // Email field (always displayed, never editable)
        ProfileFieldEntry(
            label = profileLocalization.current.fields.emailAddress,
            value = profile.email,
            isEditable = false,
            isEditing = false,
            editedValue = "",
            placeholder = "",
            onEditClick = {},
            onSaveClick = {},
            onCancelClick = {},
            onValueChange = {},
        )

        // Role-specific fields
        when (profile) {
            is DoctorProfile -> {
                ProfileFieldEntry(
                    label = profileLocalization.current.fields.fullName,
                    value = profile.fullName ?: "",
                    isEditable = true,
                    isEditing = editingField == "fullName",
                    editedValue = editedValues["fullName"] ?: "",
                    placeholder = profileLocalization.current.fields.enterFullName,
                    onEditClick = { onEvent(ProfileSettingsEvent.EditField("fullName")) },
                    onSaveClick = {
                        onEvent(
                            ProfileSettingsEvent.SaveField(
                                fieldName = "fullName",
                                onSuccess = {
                                    showSnackbar("✅ $it")
                                    refreshProfile()
                                },
                                onFailure = { showSnackbar("❌ $it") }
                            ))
                    },
                    onCancelClick = { onEvent(ProfileSettingsEvent.CancelEdit) },
                    onValueChange = { onEvent(ProfileSettingsEvent.UpdateFieldValue("fullName", it)) },
                )

                ProfileFieldEntry(
                    label = profileLocalization.current.fields.specialization,
                    value = profile.specialization ?: "",
                    isEditable = true,
                    isEditing = editingField == "specialization",
                    editedValue = editedValues["specialization"] ?: "",
                    placeholder = profileLocalization.current.fields.enterSpecialization,
                    onEditClick = { onEvent(ProfileSettingsEvent.EditField("specialization")) },
                    onSaveClick = {
                        onEvent(
                            ProfileSettingsEvent.SaveField(
                                fieldName = "specialization",
                                onSuccess = {
                                    showSnackbar("✅ $it")
                                    refreshProfile()
                                },
                                onFailure = { showSnackbar("❌ $it") }
                            ))
                    },
                    onCancelClick = { onEvent(ProfileSettingsEvent.CancelEdit) },
                    onValueChange = { onEvent(ProfileSettingsEvent.UpdateFieldValue("specialization", it)) },
                )
            }

            is OrganizationProfile -> {
                ProfileFieldEntry(
                    label = profileLocalization.current.fields.organizationName,
                    value = profile.name ?: "",
                    isEditable = true,
                    isEditing = editingField == "name",
                    editedValue = editedValues["name"] ?: "",
                    placeholder = profileLocalization.current.fields.enterOrganizationName,
                    onEditClick = { onEvent(ProfileSettingsEvent.EditField("name")) },
                    onSaveClick = {
                        onEvent(
                            ProfileSettingsEvent.SaveField(
                                fieldName = "name",
                                onSuccess = {
                                    showSnackbar("✅ $it")
                                    refreshProfile()
                                            },
                                onFailure = { showSnackbar("❌ $it") }
                            ))
                    },
                    onCancelClick = { onEvent(ProfileSettingsEvent.CancelEdit) },
                    onValueChange = { onEvent(ProfileSettingsEvent.UpdateFieldValue("name", it)) },
                )

                ProfileFieldEntry(
                    label = profileLocalization.current.fields.fieldOfActivity,
                    value = profile.field ?: "",
                    isEditable = true,
                    isEditing = editingField == "field",
                    editedValue = editedValues["field"] ?: "",
                    placeholder = profileLocalization.current.fields.enterFieldOfActivity,
                    onEditClick = { onEvent(ProfileSettingsEvent.EditField("field")) },
                    onSaveClick = {
                        onEvent(
                            ProfileSettingsEvent.SaveField(
                                fieldName = "field",
                                onSuccess = {
                                    showSnackbar("✅ $it")
                                    refreshProfile()
                                            },
                                onFailure = { showSnackbar("❌ $it") }
                            ))
                    },
                    onCancelClick = { onEvent(ProfileSettingsEvent.CancelEdit) },
                    onValueChange = { onEvent(ProfileSettingsEvent.UpdateFieldValue("field", it)) },
                )

                ProfileFieldEntry(
                    label = profileLocalization.current.fields.contactPhoneNumber,
                    value = profile.patientsContactNumber ?: "",
                    isEditable = true,
                    isEditing = editingField == "contactNumber",
                    editedValue = editedValues["contactNumber"] ?: "",
                    placeholder = profileLocalization.current.fields.enterContactPhoneNumber,
                    onEditClick = { onEvent(ProfileSettingsEvent.EditField("contactNumber")) },
                    onSaveClick = {
                        onEvent(
                            ProfileSettingsEvent.SaveField(
                                fieldName = "contactNumber",
                                onSuccess = {
                                    showSnackbar("✅ $it")
                                    refreshProfile()
                                            },
                                onFailure = { showSnackbar("❌ $it") }
                            ))
                    },
                    onCancelClick = { onEvent(ProfileSettingsEvent.CancelEdit) },
                    onValueChange = { onEvent(ProfileSettingsEvent.UpdateFieldValue("contactNumber", it)) },
                )
            }
        }
    }
}

@Composable
private fun ProfileFieldEntry(
    label: String,
    value: String,
    isEditable: Boolean,
    isEditing: Boolean,
    editedValue: String,
    placeholder: String,
    onEditClick: () -> Unit,
    onSaveClick: suspend () -> Unit,
    onCancelClick: () -> Unit,
    onValueChange: (String) -> Unit,
) {
    GeneralListEntry(
        modifier = Modifier.cursor(Cursor.Default),
        variant = HoverableListEntryVariant,
    ) {
        if (isEditing) {
            ProfileFieldEditMode(
                label = label,
                editedValue = editedValue,
                placeholder = placeholder,
                onValueChange = onValueChange,
                onSaveClick = onSaveClick,
                onCancelClick = onCancelClick,
            )
        } else {
            ProfileFieldViewMode(
                label = label,
                value = value,
                isEditable = isEditable,
                onEditClick = onEditClick,
            )
        }
    }
}

@Composable
private fun ProfileFieldViewMode(
    label: String,
    value: String,
    isEditable: Boolean,
    onEditClick: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.listEntryLeftRightSpacer)
    ) {
        UnderwrittenText(
            modifier = Modifier.weight(1f),
            topAlign = AlignSelf.Start,
            topVariant = ExuraBodyHeaderTextVariant,
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { SpanText(label) }
        ) {
            SpanText(value.ifBlank { "-" })
        }

        if (isEditable) {
            Box(
                modifier = Modifier.onClick { onEditClick() }.cursor(Cursor.Pointer)
            ) {
                SpanText(
                    commonLocalization.current.crud.edit,
                    modifier = ExuraTextStyle.toModifier(ExuraBodyHeaderTextVariant)
                )
            }
        }
    }
}

@Composable
private fun ProfileFieldEditMode(
    label: String,
    editedValue: String,
    placeholder: String,
    onValueChange: (String) -> Unit,
    onSaveClick: suspend () -> Unit,
    onCancelClick: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.listEntryLeftRightSpacer)
    ) {
        UnderwrittenText(
            modifier = Modifier.weight(1f),
            topAlign = AlignSelf.Start,
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { SpanText(label) }
        ) {
            InlineTextInput(
                value = editedValue,
                onValueChange = onValueChange,
                variant = InlineInputGhostVariant,
                placeholderText = placeholder,
                modifier = Modifier.fillMaxWidth(),
            )
        }

        Row(
            horizontalArrangement = Arrangement.spacedBy(Dimens.buttonHeight / 2),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AsyncButton(
                text = commonLocalization.current.crud.save,
                onClick = onSaveClick,
                variant = ButtonPrimaryVariant,
            )
            AppButton(
                text = commonLocalization.current.controls.cancel,
                onClick = onCancelClick,
            )
        }
    }
}
